import { fetchJson, findSource, joinUrl } from "../client.js";
import { resolveDatasetUrl } from "../sourceAliases.js";
import { chartCls, emptyStateCls } from "../chartClasses.js";
import {
  applyLineChartCatalogBounds,
  normalizeAverageReturns,
  normalizeBarChart,
  normalizeHoldings,
  normalizeHorizontalBarChart,
  normalizeLineChart,
  normalizeStatistics,
} from "../normalize.js";
import { isChartCompatible, makeIncompatibleDetail } from "../registry.js";
import type {
  ChartHandle,
  ChartType,
  ClientFetchOptions,
  IncompatibleDetail,
  NtenTabbedChartOptions,
  SourceDefinition,
  SourcesCatalog,
  TabbedChartPane,
} from "../types.js";
import { mountAverageReturns } from "./averageReturns.js";
import { renderBarChart } from "./barChart.js";
import { mountHoldings } from "./holdings.js";
import { renderHorizontalBarChart } from "./horizontalBarChart.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import { renderLineChart } from "./lineChart.js";
import { renderSplitHorizontalBarChart } from "./splitHorizontalBarChart.js";
import { mountStatistics } from "./statistics.js";
import { formatBarDisclaimerHtml, formatRichTextHtml } from "./richText.js";

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function c(suffix: string): string {
  return chartCls(`tabbed__${suffix}`);
}

/** Tabbed Characteristics / Allocations: shell H3, pane titles H4 (Liebowitz outline). */
function isCharacteristicsTabbedShell(
  shellTitle: string,
  panes: TabbedChartPane[],
): boolean {
  if (/^allocations$/i.test(String(shellTitle).trim())) return true;
  const sources = new Set(panes.map((p) => p.source));
  return (
    sources.has("portfolio_sector_allocation_bar") &&
    sources.has("portfolio_market_cap_allocation_bar")
  );
}

function formatPaneSubtitle(
  type: ChartType,
  raw: unknown,
  override?: string | null,
): string {
  if (override) return override;
  if (!raw || typeof raw !== "object") return "";
  const o = raw as Record<string, unknown>;
  const asOf = typeof o.asOf === "string" ? o.asOf : "";
  if (!asOf) return "";
  if (type === "horizontal_bar_chart" || type === "split_horizontal_bar") {
    if (typeof o.subtitle === "string" && o.subtitle.trim()) return o.subtitle;
    return `As of ${asOf} % of Total (Updated Quarterly)`;
  }
  if (type === "statistics" && typeof o.frequency === "string" && o.frequency) {
    return `As of ${asOf} (${o.frequency})`;
  }
  return `As of ${asOf}`;
}

function inferBenchmarkName(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "Benchmark Name";
  const series = (payload as { series?: { name?: string }[] }).series;
  if (!Array.isArray(series) || !series.length) return "Benchmark Name";
  const hit = series.find((s) =>
    /benchmark|index|\bs&p\b/i.test(String(s?.name ?? "")),
  );
  const name = hit?.name ?? series[series.length - 1]?.name ?? "Benchmark Name";
  return (
    String(name)
      .replace(/\s*\(%\)\s*$/i, "")
      .trim() || "Benchmark Name"
  );
}

function resolvePaneDisclaimerHtml(
  pane: TabbedChartPane,
  raw: unknown,
): string | null {
  if (
    pane.type === "bar_chart" ||
    pane.type === "line_chart" ||
    pane.type === "horizontal_bar_chart" ||
    pane.type === "split_horizontal_bar"
  ) {
    const data =
      pane.type === "bar_chart"
        ? normalizeBarChart(raw)
        : normalizeLineChart(raw);
    if (!data.disclaimer?.trim()) return null;
    return `<div class="${c("disclaimer-body")}">${formatBarDisclaimerHtml(data.disclaimer, data.benchmarkName)}</div>`;
  }
  return null;
}

export interface MountTabbedChartParams {
  container: HTMLElement;
  options: NtenTabbedChartOptions;
  catalog: SourcesCatalog;
  dataBase: string;
  fetchOptions?: ClientFetchOptions;
}

type PaneCleanup = () => void;

export async function mountTabbedChart(
  params: MountTabbedChartParams,
): Promise<ChartHandle> {
  const { container, options, catalog, dataBase, fetchOptions } = params;
  const panes = options.charts;
  if (!panes.length) {
    throw new Error(
      "ntenChart: tabbed mount requires at least one entry in charts[]",
    );
  }

  ensureAn26DesignStyles();
  const userOpts = options.options;
  const onIncompatible = userOpts?.onIncompatible;
  const shellTitle = userOpts?.title ?? "Performance";
  const isCharacteristicsShell = isCharacteristicsTabbedShell(shellTitle, panes);
  const uid = `nten-tabbed-${Math.random().toString(36).slice(2, 9)}`;
  const cache = new Map<string, unknown>();
  let activeIndex = 0;
  let paneCleanup: PaneCleanup | null = null;
  let activating = false;

  const tabsHtml = panes
    .map((pane, i) => {
      const selected = i === 0;
      return `<button
        type="button"
        role="tab"
        id="${uid}-tab-${i}"
        class="${c("tab")}${selected ? " is-active" : ""}"
        aria-selected="${selected ? "true" : "false"}"
        aria-controls="${uid}-panel"
        tabindex="${selected ? "0" : "-1"}"
      >${esc(pane.tab)}</button>`;
    })
    .join("");

  container.innerHTML = `
    <div class="${chartCls("tabbed")}">
      <div class="${c("content")}">
        <header class="an26-root__head">
        ${
          isCharacteristicsShell
            ? `<h3 class="an26-root__title">${esc(String(shellTitle))}</h3>`
            : `<h2 class="an26-root__title">${esc(String(shellTitle))}</h2>`
        }
      </header>
      <div class="${c("tabs")}" role="tablist" aria-label="${esc(String(shellTitle))} tabs">
        ${tabsHtml}
      </div>
      <div
        id="${uid}-panel"
        class="${c("panel")}"
        role="tabpanel"
        aria-labelledby="${uid}-tab-0"
        tabindex="0"
      >
        <header class="${c("pane-head")}">
          ${
            isCharacteristicsShell
              ? `<h4 class="${c("pane-title")}"></h4>`
              : `<h3 class="${c("pane-title")}"></h3>`
          }
          <p class="${c("pane-subtitle")}"></p>
        </header>
        <div class="${c("mount")}"></div>
          <aside class="${c("disclaimer")} is-hidden" hidden></aside>
        </div>
        <aside class="${c("shell-disclaimer")} is-hidden" hidden></aside>
      </div>
    </div>`;

  const tablist = container.querySelector('[role="tablist"]') as HTMLElement;
  const tabs = Array.from(
    container.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
  );
  const panel = container.querySelector(`#${uid}-panel`) as HTMLElement;
  const paneTitleEl = container.querySelector(
    `.${c("pane-title").split(/\s+/)[0]}`,
  ) as HTMLElement;
  const paneSubtitleEl = container.querySelector(
    `.${c("pane-subtitle").split(/\s+/)[0]}`,
  ) as HTMLElement;
  const mountEl = container.querySelector(
    `.${c("mount").split(/\s+/)[0]}`,
  ) as HTMLElement;
  const disclaimerEl = container.querySelector(
    `.${c("disclaimer").split(/\s+/)[0]}`,
  ) as HTMLElement;
  const shellDisclaimerEl = container.querySelector(
    `.${c("shell-disclaimer").split(/\s+/)[0]}`,
  ) as HTMLElement;

  const shellDisclaimerRaw = userOpts?.disclaimer;
  const shellDisclaimerHtml =
    shellDisclaimerRaw === null || shellDisclaimerRaw === undefined
      ? ""
      : String(shellDisclaimerRaw).trim()
        ? `<div class="${c("disclaimer-body")}">${formatRichTextHtml(String(shellDisclaimerRaw))}</div>`
        : "";
  if (shellDisclaimerHtml) {
    shellDisclaimerEl.innerHTML = shellDisclaimerHtml;
    shellDisclaimerEl.hidden = false;
    shellDisclaimerEl.classList.remove("is-hidden");
  }

  function destroyPane(): void {
    paneCleanup?.();
    paneCleanup = null;
    mountEl.replaceChildren();
  }

  function setTabUi(index: number): void {
    tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.classList.toggle("is-active", selected);
      tab.setAttribute("aria-selected", selected ? "true" : "false");
      tab.tabIndex = selected ? 0 : -1;
    });
    panel.setAttribute("aria-labelledby", `${uid}-tab-${index}`);
  }

  function showIncompatibleMessage(
    message: string,
    detail: IncompatibleDetail,
  ): void {
    console.warn(`nten-data: ${message}`);
    onIncompatible?.(detail);
    mountEl.innerHTML = `<div class="${emptyStateCls()}" style="padding:1rem;font-family:system-ui,sans-serif;font-size:14px;color:#5d6779;border:1px dashed #e7e8ec;border-radius:8px;">${esc(message)}</div>`;
    paneCleanup = () => mountEl.replaceChildren();
  }

  function mountPaneRenderer(
    pane: TabbedChartPane,
    raw: unknown,
    src: SourceDefinition,
  ): void {
    const paneTitle = pane.options?.title ?? pane.tab;
    const layout = "embedded" as const;

    if (pane.type === "bar_chart") {
      const data = normalizeBarChart(raw);
      paneCleanup = renderBarChart(mountEl, data, {
        title: paneTitle,
        subtitle: null,
        layout,
      });
      return;
    }
    if (pane.type === "line_chart") {
      const data = applyLineChartCatalogBounds(
        normalizeLineChart(raw),
        src.options as Record<string, unknown> | undefined
      );
      paneCleanup = renderLineChart(mountEl, data, {
        title: paneTitle,
        subtitle: null,
        layout,
      });
      return;
    }
    if (pane.type === "horizontal_bar_chart") {
      const data = normalizeHorizontalBarChart(raw);
      paneCleanup = renderHorizontalBarChart(mountEl, data, {
        title: paneTitle,
        subtitle: null,
        layout,
      });
      return;
    }
    if (pane.type === "split_horizontal_bar") {
      const data = normalizeHorizontalBarChart(raw);
      paneCleanup = renderSplitHorizontalBarChart(mountEl, data, {
        title: paneTitle,
        subtitle: null,
        layout,
      });
      return;
    }
    if (pane.type === "average_returns") {
      const data = normalizeAverageReturns(raw);
      paneCleanup = mountAverageReturns(mountEl, data, {
        title: paneTitle,
        disclaimer: null,
        layout,
      });
      return;
    }
    if (pane.type === "statistics") {
      const data = normalizeStatistics(raw);
      paneCleanup = mountStatistics(mountEl, data, {
        title: paneTitle,
        disclaimer: null,
        layout,
      });
      return;
    }
    if (pane.type === "holdings") {
      const data = normalizeHoldings(raw);
      const pdfUrl =
        src.metadata?.pdfStatus === "ready" &&
        typeof src.metadata?.pdfUrl === "string"
          ? joinUrl(dataBase, src.metadata.pdfUrl)
          : null;
      paneCleanup = mountHoldings(mountEl, data, {
        title: paneTitle,
        pdfUrl,
        layout,
      });
    }
  }

  async function activatePane(index: number): Promise<void> {
    if (activating) return;
    if (index === activeIndex && mountEl.childElementCount > 0 && paneCleanup)
      return;
    activating = true;
    const pane = panes[index];
    if (!pane) {
      activating = false;
      return;
    }

    destroyPane();
    activeIndex = index;
    setTabUi(index);

    paneTitleEl.textContent = pane.options?.title ?? pane.tab;

    const src = findSource(catalog, pane.source);
    if (!src) {
      const msg = `Unknown source id "${pane.source}" (tab "${pane.tab}").`;
      showIncompatibleMessage(msg, {
        ...makeIncompatibleDetail(pane.type, pane.source, "BarChart"),
        paneIndex: index,
        paneTab: pane.tab,
      });
      paneSubtitleEl.textContent = "";
      activating = false;
      return;
    }

    if (!isChartCompatible(pane.type, src.mode)) {
      const detail = makeIncompatibleDetail(pane.type, src.id, src.mode);
      detail.paneIndex = index;
      detail.paneTab = pane.tab;
      const msg = `Source "${src.id}" uses mode ${src.mode}, which is not compatible with chart type "${pane.type}" in tab "${pane.tab}". Allowed modes: ${detail.allowedModes.join(", ")}.`;
      showIncompatibleMessage(msg, detail);
      paneSubtitleEl.textContent = "";
      activating = false;
      return;
    }

    let raw: unknown;
    if (options.dataOverrides && pane.source in options.dataOverrides) {
      raw = options.dataOverrides[pane.source];
    } else if (cache.has(pane.source)) {
      raw = cache.get(pane.source);
    } else {
      const dataUrl = joinUrl(dataBase, resolveDatasetUrl(src.url));
      raw = await fetchJson<unknown>(dataUrl, fetchOptions);
      cache.set(pane.source, raw);
    }

    paneSubtitleEl.textContent = formatPaneSubtitle(
      pane.type,
      raw,
      pane.options?.subtitle ?? null,
    );
    mountPaneRenderer(pane, raw, src);

    const disclaimerHtml = resolvePaneDisclaimerHtml(pane, raw);
    if (disclaimerHtml) {
      disclaimerEl.innerHTML = disclaimerHtml;
      disclaimerEl.hidden = false;
      disclaimerEl.classList.remove("is-hidden");
    } else {
      disclaimerEl.innerHTML = "";
      disclaimerEl.hidden = true;
      disclaimerEl.classList.add("is-hidden");
    }

    activating = false;
  }

  function focusTab(index: number): void {
    const tab = tabs[index];
    if (tab) tab.focus();
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener("click", () => {
      void activatePane(i);
    });
  });

  tablist.addEventListener("keydown", (ev) => {
    const key = ev.key;
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(key)) return;
    ev.preventDefault();
    let next = activeIndex;
    if (key === "ArrowLeft")
      next = (activeIndex - 1 + panes.length) % panes.length;
    if (key === "ArrowRight") next = (activeIndex + 1) % panes.length;
    if (key === "Home") next = 0;
    if (key === "End") next = panes.length - 1;
    void activatePane(next).then(() => focusTab(next));
  });

  await activatePane(0);

  return {
    destroy() {
      destroyPane();
      container.replaceChildren();
    },
  };
}
